import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Home as HomeIcon, Building2, Briefcase, Star, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BeforeAfter } from '../components/BeforeAfter';
export const Home = () => {
  const {
    t
  } = useLanguage();
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  return <div className="w-full bg-white overflow-hidden" data-id="element-252">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col items-center text-center" data-id="element-253">
        {/* Decorative background elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-pink-light rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob" data-id="element-254"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-pink-lighter rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000" data-id="element-255"></div>

        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center" data-id="element-256">
          <img src="https://cdn.magicpatterns.com/uploads/s45KS2J3qfedmHm2iX4JF5/ChatGPT_Image_May_5,_2026,_05_53_25_PM.png" alt="Lucy Cleaning Services" className="w-full max-w-2xl object-contain mb-8" data-id="element-257" />

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-script text-pink-primary mb-8 leading-tight" data-id="element-258">
            {t('hero.tagline')}
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-259">
            <Link to="/booking" className="bg-pink-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-pink-hover transition-all shadow-pink-soft hover:shadow-pink-glow flex items-center justify-center gap-2" data-id="element-260">
              <Sparkles className="w-5 h-5" data-id="element-261" />
              {t('hero.bookNow')}
            </Link>
            <Link to="/contact" className="bg-white text-pink-primary border-2 border-pink-primary px-8 py-4 rounded-full font-semibold text-lg hover:bg-pink-50 transition-all flex items-center justify-center" data-id="element-262">
              {t('hero.getQuote')}
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Services Highlights */}
      <section className="py-20 bg-pink-lighter/30" data-id="element-263">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-264">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-265">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-266">
              {t('services.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-267">
              {t('services.subtitle')}
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-268">
            {[{
            icon: HomeIcon,
            title: t('services.house.title'),
            desc: t('services.house.desc')
          }, {
            icon: Building2,
            title: t('services.apartment.title'),
            desc: t('services.apartment.desc')
          }, {
            icon: Briefcase,
            title: t('services.office.title'),
            desc: t('services.office.desc')
          }].map((service, idx) => <motion.div key={idx} variants={fadeInUp} whileHover={{
            y: -10
          }} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-pink-soft transition-all border border-pink-50" data-id="element-269">
                <div className="w-16 h-16 bg-pink-lighter rounded-2xl flex items-center justify-center mb-6 text-pink-primary" data-id="element-270">
                  <service.icon className="w-8 h-8" data-id="element-271" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-272">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-273">
                  {service.desc}
                </p>
                <Link to="/services" className="text-pink-primary font-medium flex items-center gap-2 hover:gap-3 transition-all" data-id="element-274">
                  {t('services.learnMore')} <ArrowRight className="w-4 h-4" data-id="element-275" />
                </Link>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* About Snippet */}
      <section className="py-24" data-id="element-276">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-277">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-278">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="lg:w-1/2 relative" data-id="element-279">
              <div className="aspect-square rounded-3xl overflow-hidden relative shadow-xl" data-id="element-280">
                <img src="https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Woman cleaning a home" className="w-full h-full object-cover" data-id="element-281" />
                <div className="absolute inset-0 bg-pink-primary/10 mix-blend-overlay" data-id="element-282"></div>
              </div>
              {/* Decorative sparkle */}
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center text-pink-primary" data-id="element-283">
                <Sparkles className="w-10 h-10" data-id="element-284" />
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="lg:w-1/2" data-id="element-285">
              <h2 className="text-4xl font-script text-pink-primary mb-2" data-id="element-286">
                Lucy
              </h2>
              <h3 className="text-3xl font-bold text-navy mb-6" data-id="element-287">
                {t('about.title')}
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-8" data-id="element-288">
                {t('about.story')}
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-pink-primary transition-colors" data-id="element-289">
                {t('services.learnMore')} <ArrowRight className="w-5 h-5" data-id="element-290" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before & After */}
      <BeforeAfter data-id="element-291" />

      {/* Testimonials */}
      <section className="py-20 bg-pink-primary text-white" data-id="element-292">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-293">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-294">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-295">
              {t('testimonials.title')}
            </h2>
            <div className="w-24 h-1 bg-white/30 mx-auto rounded-full" data-id="element-296"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-297">
            {t('testimonials.reviews').map((review: any, idx: number) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} transition={{
            delay: idx * 0.2
          }} className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20" data-id="element-298">
                <div className="flex gap-1 mb-6" data-id="element-299">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-white text-white" data-id="element-300" />)}
                </div>
                <p className="text-lg mb-6 italic text-white/90" data-id="element-301">
                  "{review.text}"
                </p>
                <p className="font-semibold" data-id="element-302">{review.name}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Contact Preview CTA */}
      <section className="py-24" data-id="element-303">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-304">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-pink-lighter rounded-[3rem] p-12 md:p-16 shadow-pink-soft relative overflow-hidden" data-id="element-305">
            <Sparkles className="absolute top-8 right-8 w-12 h-12 text-pink-primary/20" data-id="element-306" />
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6" data-id="element-307">
              Ready for a spotless space?
            </h2>
            <p className="text-gray-600 text-lg mb-10 max-w-xl mx-auto" data-id="element-308">
              {t('contact.subtitle')}
            </p>
            <Link to="/booking" className="inline-flex bg-pink-primary text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-pink-hover transition-all shadow-lg hover:shadow-pink-glow items-center gap-2" data-id="element-309">
              {t('nav.bookNow')} <ArrowRight className="w-5 h-5" data-id="element-310" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
};